/* Thin client for the PEAR payments backend (the Render "doorman" service).
   The browser only ever talks to this — never to Paystack's secret API directly.
   Base URL comes from REACT_APP_PAYMENTS_URL; when unset, isPaymentsConfigured()
   is false and callers fall back to the existing (mock) flow so nothing breaks. */

import { auth } from '../firebase';

const BASE = (process.env.REACT_APP_PAYMENTS_URL || '').replace(/\/$/, '');

export function isPaymentsConfigured(): boolean {
  return !!BASE;
}

/** 'termly' = schools only: one term's amount, charged at each term start from the
 *  org's painted calendar. The rest are fixed cycles. */
export type BillingCadence = 'monthly' | 'quarterly' | 'termly' | 'yearly';
export type Currency = 'KES' | 'USD';

export interface CheckoutQuote {
  orgCode: string;
  email: string;
  /** Schools and lorries sell premium/enterprise only — `basic` was retired for
   *  them on 2026-08-21. It stays in this union because it is still the live,
   *  and only, Trasse Families plan; the server pins it per productType. */
  plan: 'basic' | 'premium' | 'enterprise';
  /** 'household' is Trasse Families: one flat KES 1,200/month subscription. */
  unit: 'child' | 'vehicle' | 'household';
  size: number;
  addonCount: number;
  cadence: BillingCadence;
  currency: Currency;
  couponCode?: string;
  /** True for the 2-week pilot: charges a small refundable verification to save the card. */
  pilot?: boolean;
}

export interface CheckoutResult {
  authorization_url: string;
  /** Powers the inline popup (resumeTransaction). */
  access_code?: string;
  reference: string;
  amountKES: number;
  currency: Currency;
}

/** Lazily load Paystack's inline v2 script once. Resolves to the PaystackPop
 *  constructor, or null if it can't load (caller falls back to redirect). */
let paystackPromise: Promise<any> | null = null;
export function loadPaystackInline(): Promise<any> {
  if (typeof window === 'undefined') return Promise.resolve(null);
  const w = window as any;
  if (w.PaystackPop) return Promise.resolve(w.PaystackPop);
  if (paystackPromise) return paystackPromise;
  paystackPromise = new Promise((resolve) => {
    const s = document.createElement('script');
    s.src = 'https://js.paystack.co/v2/inline.js';
    s.async = true;
    s.onload = () => resolve((window as any).PaystackPop || null);
    s.onerror = () => { paystackPromise = null; resolve(null); };
    document.body.appendChild(s);
  });
  return paystackPromise;
}

/** Start a hosted Paystack checkout. Returns the URL to redirect the customer to. */
export async function initializeCheckout(quote: CheckoutQuote): Promise<CheckoutResult> {
  if (!BASE) throw new Error('Payments backend not configured');
  // Come back to the "check your email" screen on THIS site after payment, so the
  // return isn't a cross-domain jump to trasseapp.com when signing up elsewhere.
  const returnUrl = typeof window !== 'undefined' ? `${window.location.origin}/billing/return` : undefined;
  const res = await fetch(`${BASE}/api/checkout/initialize`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ...quote, returnUrl }),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || 'Could not start checkout');
  return json as CheckoutResult;
}

/* ---------------------------------------------------------------------------
   Pilot → Premium "Upgrade now" — charges the card already saved during the
   pilot via the backend (auth + org isolation). These send the signed-in user's
   Firebase ID token, mirroring addonsApi. */

export interface UpgradePreview {
  ok: boolean;
  amountKES?: number;
  plan?: string;
  cadence?: BillingCadence;
  currency?: Currency;
  unit?: 'child' | 'vehicle' | 'household';
  size?: number;
  hasSavedCard?: boolean;
  /** The saved card can only be charged for a pilot (upgrade.js chargeUpgrade). */
  isPilot?: boolean;
  message?: string;
}

export interface UpgradeResult {
  ok: boolean;
  /** 'hosted' when there is no saved card / the charge declined → use hosted checkout. */
  fallback?: 'hosted';
  amountKES?: number;
  message?: string;
}

async function authedPost(path: string, body: unknown): Promise<Response> {
  const token = await auth.currentUser?.getIdToken();
  if (!token) throw new Error('not-signed-in');
  return fetch(`${BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(body),
  });
}

export interface BulkInviteCounts {
  rows: number; noEmail: number; invalidEmail: number; unpaid: number; duplicate: number;
  existing: number; recent: number; capped: number; toInvite: number;
}
export interface BulkInviteResult {
  ok: boolean;
  message?: string;
  dryRun?: boolean;
  scope?: 'paid' | 'all';
  counts?: BulkInviteCounts;
  sample?: string[];
  sent?: number;
  failed?: number;
  blocked?: number;
  otherOrg?: number;
  errors?: { email: string; reason: string }[];
}

/** Accounts → Parents → invite every parent on the fees roster by email
 *  (2026-09-29). `dryRun` only counts. Email only — never SMS. */
export async function bulkInviteParents(orgCode: string, scope: 'paid' | 'all', dryRun: boolean): Promise<BulkInviteResult> {
  if (!BASE) return { ok: false, message: 'Payments backend not configured' };
  try {
    const res = await authedPost('/api/fleet/parents/bulk-invite', { orgCode, scope, dryRun });
    const body = (await res.json().catch(() => ({}))) as BulkInviteResult;
    if (!res.ok) return { ok: false, message: body.message || `Request failed (${res.status})` };
    return body;
  } catch (e) {
    return { ok: false, message: (e as Error).message || 'Network error' };
  }
}

/** The authoritative amount + plan to show on the upgrade confirm screen. */
export async function previewUpgrade(orgCode: string): Promise<UpgradePreview> {
  if (!BASE) return { ok: false, message: 'Payments backend not configured' };
  try {
    const res = await authedPost('/api/subscription/preview', { orgCode });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) return { ok: false, message: json.message || json.error || 'Could not load pricing' };
    return json as UpgradePreview;
  } catch {
    return { ok: false, message: 'Could not load pricing' };
  }
}

/** Start the distinct "upgrade existing pilot" hosted checkout (M-Pesa / different
 *  card). Returns a Paystack authorization_url to redirect to. */
export async function initializeUpgradeCheckout(orgCode: string): Promise<{ ok: boolean; authorization_url?: string; message?: string }> {
  if (!BASE) return { ok: false, message: 'Payments backend not configured' };
  try {
    const res = await authedPost('/api/subscription/upgrade-checkout', { orgCode });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) return { ok: false, message: json.message || json.error || 'Could not start checkout' };
    return json as { ok: boolean; authorization_url?: string };
  } catch {
    return { ok: false, message: 'Could not start checkout' };
  }
}

/** Charge the saved card once and convert the pilot to a paid subscription. */
export async function upgradeNow(orgCode: string): Promise<UpgradeResult> {
  if (!BASE) return { ok: false, fallback: 'hosted', message: 'Payments backend not configured' };
  try {
    const res = await authedPost('/api/subscription/upgrade', { orgCode });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) return { ok: false, fallback: 'hosted', message: json.message || json.error || 'Upgrade failed' };
    return json as UpgradeResult;
  } catch {
    return { ok: false, fallback: 'hosted', message: 'Upgrade failed' };
  }
}

/* ---------------------------------------------------------------------------
   Paid→paid PRORATED upgrade (basic → premium/enterprise). Charges only the tier
   difference for the days left in the current period, then rewrites billingQuote
   so every future renewal bills the new tier. Replaces the old dashboard mock
   that flipped subscriptionPlan for free. Crossing into premium wakes Zana. */

export interface ProrationPreview extends UpgradePreview {
  fromPlan?: string;
  daysRemaining?: number;
  periodDays?: number;
  newFullTermKES?: number;
  lineSummary?: string;
}

export interface ProrationResult extends UpgradeResult {
  /** Present on the hosted-fallback path — redirect the admin here to pay the diff. */
  authorization_url?: string;
  plan?: string;
  applied?: 'instant';
}

/** The exact prorated amount + summary to show before charging. */
export async function previewUpgradeProration(orgCode: string, plan: 'premium' | 'enterprise'): Promise<ProrationPreview> {
  if (!BASE) return { ok: false, message: 'Payments backend not configured' };
  try {
    const res = await authedPost('/api/subscription/upgrade-proration/preview', { orgCode, plan });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) return { ok: false, message: json.message || json.error || 'Could not compute the upgrade amount' };
    return json as ProrationPreview;
  } catch {
    return { ok: false, message: 'Could not compute the upgrade amount' };
  }
}

/** Charge the prorated diff on the saved card and lift the plan immediately. */
export async function upgradeProration(orgCode: string, plan: 'premium' | 'enterprise'): Promise<ProrationResult> {
  if (!BASE) return { ok: false, fallback: 'hosted', message: 'Payments backend not configured' };
  try {
    const res = await authedPost('/api/subscription/upgrade-proration', { orgCode, plan });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) return { ok: false, fallback: 'hosted', message: json.message || json.error || 'Upgrade failed' };
    return json as ProrationResult;
  } catch {
    return { ok: false, fallback: 'hosted', message: 'Upgrade failed' };
  }
}

/* ---------------------------------------------------------------------------
   Cancel / resume. Policy: cancel before the next billing → that cycle is never
   charged; access continues to the end of the paid period; no refunds for
   periods already billed. */

export interface CancelResult {
  ok: boolean;
  alreadyCanceled?: boolean;
  /** ISO date / Firestore timestamp of when access ends (current paid period). */
  accessUntil?: unknown;
  message?: string;
}

export async function cancelSubscription(orgCode: string, reason: string): Promise<CancelResult> {
  if (!BASE) return { ok: false, message: 'Payments backend not configured' };
  try {
    const res = await authedPost('/api/subscription/cancel', { orgCode, reason });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) return { ok: false, message: json.message || json.error || 'Could not cancel' };
    return json as CancelResult;
  } catch {
    return { ok: false, message: 'Could not cancel — check your connection and try again.' };
  }
}

export async function resumeSubscription(orgCode: string): Promise<{ ok: boolean; message?: string }> {
  if (!BASE) return { ok: false, message: 'Payments backend not configured' };
  try {
    const res = await authedPost('/api/subscription/resume', { orgCode });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) return { ok: false, message: json.message || json.error || 'Could not resume' };
    return json as { ok: boolean; message?: string };
  } catch {
    return { ok: false, message: 'Could not resume — check your connection and try again.' };
  }
}

/* ------------------------------------------- console cancellation feed ---- */

export interface CancellationItem {
  id: string;
  orgCode: string;
  orgName: string;
  plan: string | null;
  cadence: string | null;
  reason: string | null;
  requestedBy: string | null;
  followUpDone: boolean;
  at: string | null;
  accessUntil: string | null;
}

/** Platform-console only (backend enforces platform-admin).
 *  Returns ok:false on failure so the UI can show "couldn't load" instead of
 *  rendering an outage identically to "no cancellations". */
export async function fetchCancellations(): Promise<{ ok: boolean; items: CancellationItem[] }> {
  if (!BASE) return { ok: true, items: [] };
  try {
    const res = await authedPost('/api/console/cancellations', {});
    const json = await res.json().catch(() => ({}));
    if (res.ok && Array.isArray(json.items)) return { ok: true, items: json.items as CancellationItem[] };
    return { ok: false, items: [] };
  } catch {
    return { ok: false, items: [] };
  }
}

export async function markCancellationDone(id: string): Promise<boolean> {
  if (!BASE) return false;
  try {
    const res = await authedPost('/api/console/cancellations/done', { id });
    return res.ok;
  } catch {
    return false;
  }
}

/** Console: re-send the activation (sign-in) email to a pending org's admin. */
export async function resendActivation(orgCode: string): Promise<{ ok: boolean; email?: string; message?: string }> {
  if (!BASE) return { ok: false, message: 'Payments backend not configured' };
  try {
    const res = await authedPost('/api/console/resend-activation', { orgCode });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) return { ok: false, message: json.message || json.error || 'Could not resend the invite.' };
    return json as { ok: boolean; email?: string };
  } catch {
    return { ok: false, message: 'Could not resend — check your connection and try again.' };
  }
}

/** Self-serve post-payment screen: resend the branded Brevo activation email (not Firebase client). */
export async function resendActivationSelfServe(
  orgCode: string,
  email: string,
): Promise<{ ok: boolean; email?: string; message?: string }> {
  if (!BASE) return { ok: false, message: 'Payments backend not configured' };
  try {
    const res = await fetch(`${BASE}/api/activation/resend`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ orgCode, email: email.trim().toLowerCase() }),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) return { ok: false, message: json.message || json.error || 'Could not resend the link.' };
    return json as { ok: boolean; email?: string };
  } catch {
    return { ok: false, message: 'Could not resend — check your connection and try again.' };
  }
}

/** Fleet Accounts → invite driver/parent. Branded Brevo email (not Firebase client). */
export async function sendFleetInviteEmailApi(params: {
  orgCode: string;
  email: string;
  role: string;
  orgName: string;
  inviteId: string;
  continueUrl: string;
}): Promise<{ ok: boolean; error?: string }> {
  if (!BASE) return { ok: false, error: 'Payments backend not configured' };
  try {
    const res = await authedPost('/api/fleet/invite-email', params);
    const json = await res.json().catch(() => ({}));
    if (!res.ok) return { ok: false, error: json.message || json.error || 'Could not send the invite email.' };
    return { ok: true };
  } catch {
    return { ok: false, error: 'Could not send invite — check your connection and try again.' };
  }
}

/** Team → invite fleet admin. Branded Brevo email (not Firebase client). */
export async function sendAdminInviteEmailApi(params: {
  orgCode: string;
  email: string;
  continueUrl: string;
}): Promise<{ ok: boolean; error?: string; link?: string }> {
  if (!BASE) return { ok: false, error: 'Payments backend not configured' };
  try {
    const res = await authedPost('/api/fleet/admin-invite-email', params);
    const json = await res.json().catch(() => ({}));
    if (!res.ok) return { ok: false, error: json.message || json.error || 'Could not send the admin invite.' };
    return { ok: true, link: typeof json.link === 'string' ? json.link : undefined };
  } catch {
    return { ok: false, error: 'Could not send invite — check your connection and try again.' };
  }
}

/** Dashboard/mobile signup verification. Branded Brevo (not Firebase sendEmailVerification). */
export async function sendVerificationEmailApi(continueUrl?: string): Promise<{ ok: boolean; error?: string }> {
  if (!BASE) return { ok: false, error: 'Payments backend not configured' };
  try {
    const res = await authedPost('/api/auth/verification-email', {
      continueUrl: continueUrl || (typeof window !== 'undefined' ? window.location.origin : undefined),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) return { ok: false, error: json.message || json.error || 'Could not send verification email.' };
    return { ok: true };
  } catch {
    return { ok: false, error: 'Could not send verification — check your connection and try again.' };
  }
}

/** True when the identifier the user typed is a username rather than an email. */
export function looksLikeUsername(identifier: string): boolean {
  const v = (identifier || '').trim();
  return v.length > 0 && !v.includes('@');
}

/** Swap a login username for the email behind it. Firebase Auth only signs in by
 *  email, so the login form calls this first when the field has no '@'. The
 *  password is never sent here — only the name being looked up. */
export async function lookupOrgByCode(code: string): Promise<{ ok: boolean; org?: { code: string; name: string; productType?: string | null }; error?: string }> {
  const base = BASE || 'https://pear-payments.onrender.com';
  const trimmed = (code || '').trim();
  if (!trimmed) return { ok: false, error: 'Enter an organization code.' };
  try {
    const res = await fetch(`${base}/api/orgs/lookup`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code: trimmed }),
    });
    const json = await res.json().catch(() => ({} as { message?: string; org?: { code: string; name: string } }));
    if (!res.ok || !json.org) return { ok: false, error: json.message || 'Unknown organization code.' };
    return { ok: true, org: json.org };
  } catch {
    return { ok: false, error: 'Could not reach the sign-in service — check your connection and try again.' };
  }
}

/* The web login's fleet-admin one-time code, asked instead of queried
 * (2026-09-17). Login.tsx LISTED fleet_admin_signup_codes where code == the
 * digits, signed out — which meant anyone could list the whole collection,
 * every org mid-onboarding with its live code. The rules keep it to PEAR admins
 * now; the server answers "is this the live code for THIS org", yes or no — the
 * digits alone never name an org. 'unknown' is its own answer, so a sleeping
 * backend never reads as a wrong code, and neither does 'busy': the org's
 * guesses are spent, which says nothing about these four digits. */
export type AdminCodeCheck =
  | { state: 'valid'; orgCode: string }
  | { state: 'invalid' }
  | { state: 'too_many' }
  | { state: 'busy' }
  | { state: 'unknown' };

/** One reply turned into an answer. Pure, so it is tested without a network. */
export function parseAdminCodeCheck(status: number, json: unknown): AdminCodeCheck {
  const j = (json && typeof json === 'object' ? json : {}) as {
    ok?: unknown; valid?: unknown; orgCode?: unknown; reason?: unknown;
  };
  if (status === 503 && j.reason === 'busy') return { state: 'busy' };
  if (status !== 200 || j.ok !== true) return { state: 'unknown' };
  if (j.valid === true && typeof j.orgCode === 'string' && j.orgCode.trim()) {
    return { state: 'valid', orgCode: j.orgCode.trim() };
  }
  if (j.valid === false) return j.reason === 'too_many' ? { state: 'too_many' } : { state: 'invalid' };
  return { state: 'unknown' };
}

export async function checkAdminSignupCode(orgCode: string, code: string): Promise<AdminCodeCheck> {
  const base = BASE || 'https://pear-payments.onrender.com';
  try {
    const res = await fetch(`${base}/api/orgs/admin-code/check`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ orgCode: (orgCode || '').trim(), code: (code || '').trim() }),
    });
    const json = await res.json().catch(() => null);
    return parseAdminCodeCheck(res.status, json);
  } catch {
    return { state: 'unknown' };
  }
}

export async function loadPendingActivationPayload(): Promise<{ ok: boolean; payload?: Record<string, unknown>; error?: string }> {
  const base = BASE || 'https://pear-payments.onrender.com';
  try {
    const token = await auth.currentUser?.getIdToken();
    if (!token) return { ok: false, error: 'not-signed-in' };
    const res = await fetch(`${base}/api/orgs/pending-activation`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({}),
    });
    const json = await res.json().catch(() => ({} as { message?: string; payload?: Record<string, unknown> }));
    if (!res.ok || !json.payload) return { ok: false, error: json.message || 'No pending organization.' };
    return { ok: true, payload: json.payload };
  } catch (e) {
    return { ok: false, error: (e as Error).message || 'Could not load signup.' };
  }
}

export async function resolveUsernameApi(username: string): Promise<{ ok: boolean; email?: string; error?: string }> {
  if (!BASE) return { ok: false, error: 'Username sign-in is unavailable here — use your email address.' };
  try {
    const res = await fetch(`${BASE}/api/auth/resolve-username`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username: (username || '').trim().toLowerCase() }),
    });
    const json = await res.json().catch(() => ({} as any));
    if (!res.ok || !json.email) return { ok: false, error: json.message || 'That username was not recognised.' };
    return { ok: true, email: String(json.email) };
  } catch {
    return { ok: false, error: 'Could not reach the sign-in service — check your connection and try again.' };
  }
}

/** Branded forgot-password email via Brevo (Firebase reset link). Same endpoint
 *  the mobile app uses. Always reports ok to the UI when the server does — the
 *  backend does not leak whether the address exists. */
export async function sendPasswordResetEmailApi(email: string): Promise<{ ok: boolean; error?: string }> {
  const base = BASE || 'https://pear-payments.onrender.com';
  const trimmed = (email || '').trim().toLowerCase();
  if (!trimmed || !trimmed.includes('@')) {
    return { ok: false, error: 'Enter the email address for your account.' };
  }
  try {
    const continueUrl =
      typeof window !== 'undefined' ? `${window.location.origin}/login` : 'https://trasseapp.com/login';
    const res = await fetch(`${base}/api/auth/password-reset-email`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: trimmed, continueUrl }),
    });
    const json = await res.json().catch(() => ({} as { message?: string; error?: string }));
    if (!res.ok) return { ok: false, error: json.message || json.error || 'Could not send reset email.' };
    return { ok: true };
  } catch {
    return { ok: false, error: 'Could not send reset email — check your connection and try again.' };
  }
}

/** Dev/unpaid onboarding: branded sign-in link via Brevo (not Firebase client).
 *  Uses payments URL when set; otherwise the production doorman so local unpaid
 *  signup still gets PEAR Fleet mail instead of Firebase spam. */
export async function sendSignInLinkEmailApi(params: {
  email: string;
  continueUrl: string;
}): Promise<{ ok: boolean; error?: string }> {
  const base = BASE || 'https://pear-payments.onrender.com';
  try {
    const res = await fetch(`${base}/api/auth/sign-in-link-email`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) return { ok: false, error: json.message || json.error || 'Could not send the login link.' };
    return { ok: true };
  } catch {
    return { ok: false, error: 'Could not send link — check your connection and try again.' };
  }
}

/** Preview whether a debug coupon is live and what it reduces the charge to. */
export async function validateCoupon(code: string): Promise<{ valid: boolean; amountKES?: number }> {
  if (!BASE || !code) return { valid: false };
  try {
    const res = await fetch(`${BASE}/api/coupons/validate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code }),
    });
    return await res.json();
  } catch {
    return { valid: false };
  }
}

/* ── Enterprise multi-branch ──────────────────────────────────────────────
   A "branch" is a full organization of its own (own buses, students, drivers,
   logins) linked to a mother Enterprise org by enterpriseGroupId. Billing is
   per branch on its own size, prorated onto the MOTHER's renewal date so the
   whole group shares one cycle. Only Enterprise orgs may add branches. */

export interface BranchQuote {
  ok: boolean;
  /** Prorated amount charged today to align this branch to the group cycle. */
  amountKES?: number;
  /** What the branch adds to every future cycle once aligned. */
  recurringKES?: number;
  daysRemaining?: number;
  /** ISO date the whole group renews on. */
  renewsOn?: string;
  /** True for console-created accounts billed off-platform: no charge here. */
  manual?: boolean;
  message?: string;
}

export interface BranchResult {
  ok: boolean;
  orgCode?: string;
  /** Set when there's no reusable card and the diff must be paid on Paystack. */
  fallback?: 'hosted';
  authorization_url?: string;
  message?: string;
}

/** Price a prospective branch without creating anything. */
export async function previewAddBranch(
  orgCode: string, size: number,
): Promise<BranchQuote> {
  if (!BASE) return { ok: false, message: 'Payments backend not configured' };
  try {
    const res = await authedPost('/api/subscription/add-branch/preview', { orgCode, size });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) return { ok: false, message: json.message || json.error || 'Could not price that branch' };
    return json as BranchQuote;
  } catch {
    return { ok: false, message: 'Could not price that branch' };
  }
}

/** Create the branch org, link it to the group, and charge the prorated diff. */
export async function addBranch(
  orgCode: string, branch: { name: string; size: number; productType?: string },
): Promise<BranchResult> {
  if (!BASE) return { ok: false, message: 'Payments backend not configured' };
  try {
    const res = await authedPost('/api/subscription/add-branch', { orgCode, ...branch });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) return { ok: false, message: json.message || json.error || 'Could not create the branch' };
    return json as BranchResult;
  } catch {
    return { ok: false, message: 'Could not create the branch' };
  }
}

export interface BranchOrgRow {
  code: string;
  name: string;
  isMother?: boolean;
  plan?: string | null;
  parentOrgCode?: string | null;
  /** Its place in the group, 1 = the mother; an Enterprise campus from the 4th pays the higher rate. */
  campus?: number;
}

export interface BranchListResult {
  ok: boolean;
  groupId?: string | null;
  orgs?: BranchOrgRow[];
  message?: string;
}

/** Every org in this enterprise group (mother + branches). */
export async function listBranches(orgCode: string): Promise<BranchListResult> {
  if (!BASE) return { ok: false, message: 'Payments backend not configured' };
  try {
    const res = await authedPost('/api/subscription/branches', { orgCode });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) return { ok: false, message: json.message || json.error || 'Could not load branches' };
    return json as BranchListResult;
  } catch {
    return { ok: false, message: 'Could not load branches' };
  }
}

/** Re-point this login at another org in the enterprise group. */
export async function switchBranch(
  targetOrgCode: string,
): Promise<{ ok: boolean; orgCode?: string; name?: string; message?: string }> {
  if (!BASE) return { ok: false, message: 'Payments backend not configured' };
  try {
    const res = await authedPost('/api/subscription/switch-branch', { targetOrgCode });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) return { ok: false, message: json.message || json.error || 'Could not switch branch' };
    return json as { ok: boolean; orgCode?: string; name?: string; message?: string };
  } catch {
    return { ok: false, message: 'Could not switch branch' };
  }
}

/* ── org purge: the console's Delete, on the server ─────────────────────────
   Every delete happens server-side, logins included — a browser cannot delete
   someone else's login, and cannot see half the collections an org's data
   lives in. Only payment records are kept. A small school takes a few seconds; there is no client
   timeout on purpose, because giving up early says nothing about whether the
   delete finished. */
export async function purgeOrgOnServer(
  orgCode: string,
  orgDocId?: string,
): Promise<{ ok: boolean; counts?: Record<string, number>; authFailed?: number; message?: string }> {
  if (!BASE) return { ok: false, message: 'the payments backend is not configured' };
  try {
    const res = await authedPost('/api/console/purge-org', { orgCode, ...(orgDocId ? { orgDocId } : {}) });
    const json = await res.json().catch(() => ({}));
    if (!res.ok || json.ok !== true) {
      return { ok: false, message: json.message || json.error || `the server answered ${res.status}` };
    }
    return { ok: true, counts: json.counts || {}, authFailed: Number(json.auth?.failed || 0) };
  } catch {
    return { ok: false, message: 'could not reach the server' };
  }
}

/* ── Trasse Families: the 14-day, no-card pilot ─────────────────────────────
   A household signs up with no card at all, so `start` is public and rate
   limited server-side, and `activate` is the authenticated half — the Firebase
   email link is the only proof that the mailbox (and therefore the household)
   belongs to the person clicking. Both fall back to the production doorman when
   REACT_APP_PAYMENTS_URL is unset: without a base URL the signup page is simply
   dead, and a dead signup page is worse than a cross-origin call. */

const FAMILIES_BASE = BASE || 'https://pear-payments.onrender.com';

export type FamilyDriverMode = 'private_driver' | 'parent_drives' | 'private_van';

export interface FamilyChildInput {
  name: string;
  classLabel?: string;
}

export interface FamilyPilotStartBody {
  parentName: string;
  email: string;
  phone: string;
  householdName: string;
  children: FamilyChildInput[];
  driverMode: FamilyDriverMode;
  driverName?: string;
  driverPhone?: string;
  driverEmail?: string;
  vanCompany?: string;
  vanSeats?: number;
  vanPhone?: string;
  vehiclePlate: string;
  vehicleNickname?: string;
  fuelType?: string;
  home: { lat: number; lng: number };
  school: { lat: number; lng: number } | null;
  schoolName?: string;
  schoolCode?: string;
  schedule: { morningAt: string; eveningAt: string; days?: string[]; /** both, or only one run (2026-10-28) */ runs?: 'both' | 'morning' | 'evening' };
  referralCode?: string;
}

export interface FamilyPilotStartResult {
  ok: boolean;
  orgCode?: string;
  email?: string;
  pilotDays?: number;
  emailed?: boolean;
  /** HTTP status on failure — 409 means the email/phone/name is already taken. */
  status?: number;
  /** The input that was rejected. Render `message` VERBATIM against this field. */
  field?: string;
  message?: string;
}

/** Are this phone, email, household name, or plate already taken?
 *  Continue on a step calls this so a conflict is told there, not after the
 *  activation link. Only the fields sent are checked; email and phone are only
 *  refused together (checkFamilySignup in pilot.js). A missing status means we
 *  never heard back — the caller should not trap the parent on that, nor on
 *  anything but a 400/409 that names a field. */
export async function checkFamilySignup(body: {
  email?: string;
  phone?: string;
  householdName?: string;
  vehiclePlate?: string;
}): Promise<FamilyPilotStartResult> {
  try {
    const res = await fetch(`${FAMILIES_BASE}/api/families/pilot/check`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const json = await res.json().catch(() => ({} as Record<string, unknown>));
    if (!res.ok || json.ok === false) {
      return {
        ok: false,
        status: res.status,
        field: typeof json.field === 'string' ? json.field : undefined,
        // `message` only. The route's 500 handler puts the raw exception text
        // in `error`, and that must never reach a parent (review, 2026-09-14).
        message: (typeof json.message === 'string' && json.message) || 'We could not check that just now.',
      };
    }
    return { ok: true };
  } catch {
    return { ok: false, message: 'Could not reach Trasse — check your connection and try again.' };
  }
}

/** Create the pending household and send the activation email. No auth, no card. */
export async function startFamilyPilot(body: FamilyPilotStartBody): Promise<FamilyPilotStartResult> {
  try {
    const res = await fetch(`${FAMILIES_BASE}/api/families/pilot/start`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const json = await res.json().catch(() => ({} as Record<string, unknown>));
    if (!res.ok || json.ok === false) {
      return {
        ok: false,
        status: res.status,
        field: typeof json.field === 'string' ? json.field : undefined,
        // The server's wording is the product's wording — pass it through, never
        // paraphrase it into a generic "something went wrong". A 5xx `error` is
        // not wording, it is the raw exception text (review, 2026-09-14).
        message:
          (typeof json.message === 'string' && json.message) ||
          (res.status < 500 && typeof json.error === 'string' && json.error) ||
          'We could not start your free days. Please try again.',
      };
    }
    return json as FamilyPilotStartResult;
  } catch {
    return { ok: false, message: 'Could not reach Trasse — check your connection and try again.' };
  }
}

export interface FamilyPilotActivateResult {
  ok: boolean;
  orgCode?: string;
  pilotDays?: number;
  driverMode?: FamilyDriverMode;
  pendingDriver?: { name: string; email: string | null; phone: string | null } | null;
  alreadyActive?: boolean;
  message?: string;
}

/** Finish the household after the email link proved the mailbox. Requires auth. */
export async function activateFamilyPilot(orgCode: string): Promise<FamilyPilotActivateResult> {
  try {
    const token = await auth.currentUser?.getIdToken();
    if (!token) return { ok: false, message: 'Your session expired. Reopen the link from your email.' };
    const res = await fetch(`${FAMILIES_BASE}/api/families/pilot/activate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ orgCode }),
    });
    const json = await res.json().catch(() => ({} as Record<string, unknown>));
    if (!res.ok || json.ok === false) {
      return {
        ok: false,
        // Activation is the call with the most writes, so the likeliest to 500,
        // and a 5xx `error` is the raw exception text — a Firestore error
        // string on the parent's screen (review, 2026-09-14).
        message:
          (typeof json.message === 'string' && json.message) ||
          (res.status < 500 && typeof json.error === 'string' && json.error) ||
          'Could not start your free days. Please try again.',
      };
    }
    return json as FamilyPilotActivateResult;
  } catch {
    return { ok: false, message: 'Could not reach Trasse — check your connection and try again.' };
  }
}

export interface FamilyResendResult {
  ok: boolean;
  /** True only when a mail actually went out. `false` is NOT a failure: an
   *  address the server does not recognise gets the same `{ok:true, emailed:false}`
   *  on purpose, so this endpoint can't be used to test which households exist.
   *  Callers must show one confirmation for both. */
  emailed?: boolean;
  /** HTTP status on failure — 409 means the household is already set up. */
  status?: number;
  /** The server's wording. Render it VERBATIM. */
  message?: string;
}

/** Send the activation link again. Public and rate limited server-side (3/hour
 *  per email); it only ever mails the address already on the household, so a
 *  wrong guess reaches the rightful owner rather than the guesser. */
export interface FamilyWaitlistResult {
  ok: boolean;
  kind?: string;
  message?: string;
}

/** Join the shared-van waitlist. A household is NOT created — this is a mailing
 *  list for a product that does not exist yet. Previously this posted to the
 *  pilot endpoint, which rejected it and stored nothing, so the address the
 *  parent gave us was asked for and then discarded. */
export async function joinFamilyWaitlist(
  body: { email: string; phone?: string; parentName?: string; householdName?: string },
): Promise<FamilyWaitlistResult> {
  try {
    const res = await fetch(`${FAMILIES_BASE}/api/families/waitlist`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...body, kind: 'private_van' }),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok || json.ok === false) {
      return { ok: false, message: json.message || 'We could not save that just now.' };
    }
    return { ok: true, kind: json.kind };
  } catch {
    return { ok: false, message: 'You appear to be offline — try again in a moment.' };
  }
}

export async function resendFamilyActivation(
  email: string,
  orgCode?: string,
): Promise<FamilyResendResult> {
  try {
    const res = await fetch(`${FAMILIES_BASE}/api/families/resend-activation`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email: (email || '').trim().toLowerCase(),
        orgCode: (orgCode || '').trim() || undefined,
      }),
    });
    const json = await res.json().catch(() => ({} as Record<string, unknown>));
    if (!res.ok || json.ok === false) {
      return {
        ok: false,
        status: res.status,
        message:
          (typeof json.message === 'string' && json.message) ||
          (res.status < 500 && typeof json.error === 'string' && json.error) ||
          'We could not send that email again. Please try in a few minutes.',
      };
    }
    return json as FamilyResendResult;
  } catch {
    return { ok: false, message: 'Could not reach Trasse — check your connection and try again.' };
  }
}

/* ── The household's one driver ────────────────────────────────────────────
   Both endpoints run on the Admin SDK because a household cannot be trusted to
   mint an auth account from the browser. The parent's own ID token proves they
   own the org; the server checks it against the household before touching
   anything. See pear-payments-backend/src/families/driver.js. */

export interface FamilyDriverResult {
  ok: boolean;
  uid?: string;
  email?: string;
  /** Present ONLY when the account was newly created. An existing Trasse user
   *  keeps the password they already have, and we must not imply otherwise. */
  password?: string;
  created?: boolean;
  status?: number;
  message?: string;
}

/** Invite the driver. `replace` is required once a driver already exists — the
 *  server refuses with 409 otherwise, which is the confirmation step. */
export async function inviteFamilyDriver(params: {
  orgCode: string;
  email: string;
  displayName?: string;
  replace?: boolean;
}): Promise<FamilyDriverResult> {
  try {
    const token = await auth.currentUser?.getIdToken();
    if (!token) return { ok: false, message: 'Your session expired. Sign in again.' };
    const res = await fetch(`${FAMILIES_BASE}/api/families/driver/create`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({
        orgCode: params.orgCode,
        email: (params.email || '').trim().toLowerCase(),
        displayName: (params.displayName || '').trim(),
        replace: params.replace === true,
      }),
    });
    const json = await res.json().catch(() => ({} as Record<string, unknown>));
    if (!res.ok || json.ok === false) {
      return {
        ok: false,
        status: res.status,
        // The server's wording is the product's wording — "Your car already has
        // a driver" is the sentence the parent should read, verbatim.
        message:
          (typeof json.message === 'string' && json.message) ||
          (typeof json.error === 'string' && json.error) ||
          'We could not invite your driver. Please try again.',
      };
    }
    return json as FamilyDriverResult;
  } catch {
    return { ok: false, message: 'Could not reach Trasse — check your connection and try again.' };
  }
}

/** Remove the driver. Detaches; never deletes — the runs they drove are the
 *  family's record. */
export async function removeFamilyDriver(orgCode: string, uid: string): Promise<FamilyDriverResult> {
  try {
    const token = await auth.currentUser?.getIdToken();
    if (!token) return { ok: false, message: 'Your session expired. Sign in again.' };
    const res = await fetch(`${FAMILIES_BASE}/api/families/driver/remove`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ orgCode, uid }),
    });
    const json = await res.json().catch(() => ({} as Record<string, unknown>));
    if (!res.ok || json.ok === false) {
      return {
        ok: false,
        status: res.status,
        message:
          (typeof json.message === 'string' && json.message) ||
          (typeof json.error === 'string' && json.error) ||
          'We could not remove your driver. Please try again.',
      };
    }
    return json as FamilyDriverResult;
  } catch {
    return { ok: false, message: 'Could not reach Trasse — check your connection and try again.' };
  }
}

/** A4: seat cap hit — fleetAlert + admin email. Best-effort; Parent Zana UX is Claude. */
export async function notifyCapacityReached(params: {
  orgCode: string;
  maxStudents?: number;
  currentStudents?: number;
  source?: string;
}): Promise<{ ok: boolean; skipped?: boolean }> {
  const base = BASE || 'https://pear-payments.onrender.com';
  try {
    const token = await auth.currentUser?.getIdToken();
    if (!token) return { ok: false };
    const res = await fetch(`${base}/api/orgs/notify-capacity`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(params),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) return { ok: false };
    return json as { ok: boolean; skipped?: boolean };
  } catch {
    return { ok: false };
  }
}

/* ── Trasse Families: referrals ────────────────────────────────────────────
   Both of these need the doorman because firestore.rules closes referralCodes
   to LISTING by design — the code is the secret, fetchable by id and never
   enumerable. So a parent cannot query for their own code, and the signed-out
   signup page cannot read one at all. */

export interface FamilyReferralCode {
  ok: boolean;
  code?: string;
  /** False while REFERRALS_PAY_ENABLED is off — the code works and the
   *  introduction is recorded, but no reward is promised on screen. */
  payEnabled?: boolean;
  message?: string;
}

export async function fetchMyFamilyReferralCode(orgCode: string): Promise<FamilyReferralCode> {
  try {
    const token = await auth.currentUser?.getIdToken();
    if (!token) return { ok: false, message: 'Your session expired. Sign in again.' };
    const res = await fetch(
      `${FAMILIES_BASE}/api/families/referral/mine?orgCode=${encodeURIComponent(orgCode)}`,
      { headers: { Authorization: `Bearer ${token}` } },
    );
    const json = await res.json().catch(() => ({} as Record<string, unknown>));
    if (!res.ok || json.ok === false) {
      return {
        ok: false,
        message: (typeof json.message === 'string' && json.message) || 'Could not load your invite code.',
      };
    }
    return json as FamilyReferralCode;
  } catch {
    return { ok: false, message: 'Could not reach Trasse — check your connection and try again.' };
  }
}

/** Public: who is inviting this signup. Label only — never an email or phone. */
export async function lookupFamilyReferrer(
  code: string,
): Promise<{ ok: boolean; label?: string; kind?: 'school' | 'parent' }> {
  try {
    const clean = (code || '').trim().toUpperCase();
    if (!clean) return { ok: false };
    const res = await fetch(`${FAMILIES_BASE}/api/families/referral/${encodeURIComponent(clean)}`);
    if (!res.ok) return { ok: false };
    const json = await res.json().catch(() => ({}));
    return json as { ok: boolean; label?: string; kind?: 'school' | 'parent' };
  } catch {
    return { ok: false };
  }
}

/* ── Trasse Families: linking a school ─────────────────────────────────────
   The one cross-org arrangement in the product. Rules let both sides READ the
   link and neither write it, so every transition goes through the doorman,
   where the consent check runs — in particular, the caller's SIDE is resolved
   from their own user document and never from anything sent here. */

export type SchoolLinkStatus = 'requested' | 'accepted' | 'declined' | 'revoked';

export interface FamilySchoolLink {
  linkId: string;
  familyOrgCode: string;
  schoolCode: string;
  schoolName: string;
  familyName: string;
  status: SchoolLinkStatus;
  childIds: string[];
  children?: { childId: string; childName: string }[];
  parentUid?: string | null;
  revokedBy: 'family' | 'school' | null;
}

export async function requestSchoolLink(params: {
  orgCode: string;
  schoolCode: string;
  childIds?: string[];
}): Promise<{ ok: boolean; status?: number; schoolName?: string; message?: string }> {
  try {
    const token = await auth.currentUser?.getIdToken();
    if (!token) return { ok: false, message: 'Your session expired. Sign in again.' };
    const res = await fetch(`${FAMILIES_BASE}/api/families/school-link/request`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(params),
    });
    const json = await res.json().catch(() => ({} as Record<string, unknown>));
    if (!res.ok || json.ok === false) {
      return {
        ok: false,
        status: res.status,
        // The server's wording is the product's wording — "That code belongs to
        // another family, not a school" is the sentence to show, verbatim.
        message: (typeof json.message === 'string' && json.message)
          || 'We could not send that request.',
      };
    }
    return json as { ok: boolean; schoolName?: string };
  } catch {
    return { ok: false, message: 'Could not reach Trasse — check your connection and try again.' };
  }
}

/** accept | decline | revoke. What the caller is allowed to do is decided
 *  server-side from which side of the link their org is on. */
export async function respondToSchoolLink(
  linkId: string,
  action: 'accept' | 'decline' | 'revoke',
): Promise<{ ok: boolean; status?: SchoolLinkStatus; message?: string }> {
  try {
    const token = await auth.currentUser?.getIdToken();
    if (!token) return { ok: false, message: 'Your session expired. Sign in again.' };
    const res = await fetch(`${FAMILIES_BASE}/api/families/school-link/respond`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ linkId, action }),
    });
    const json = await res.json().catch(() => ({} as Record<string, unknown>));
    if (!res.ok || json.ok === false) {
      return {
        ok: false,
        message: (typeof json.message === 'string' && json.message) || 'That did not go through.',
      };
    }
    return json as { ok: boolean; status?: SchoolLinkStatus };
  } catch {
    return { ok: false, message: 'Could not reach Trasse — check your connection and try again.' };
  }
}

/** Every link an org is a party to — a household's schools, or a school's
 *  households. Org-scoped server-side. */
export async function listSchoolLinks(
  orgCode: string,
): Promise<{ ok: boolean; links: FamilySchoolLink[] }> {
  try {
    const token = await auth.currentUser?.getIdToken();
    if (!token) return { ok: false, links: [] };
    const res = await fetch(
      `${FAMILIES_BASE}/api/families/school-link/list?orgCode=${encodeURIComponent(orgCode)}`,
      { headers: { Authorization: `Bearer ${token}` } },
    );
    const json = await res.json().catch(() => ({} as Record<string, unknown>));
    if (!res.ok || json.ok === false) return { ok: false, links: [] };
    return { ok: true, links: (json.links as FamilySchoolLink[]) || [] };
  } catch {
    return { ok: false, links: [] };
  }
}

/** Put a linked household's child on a school trip. No temp login — the parent
 *  already has a Trasse account. Same consent gate as the rest of the link. */
export async function addLinkedChildToTrip(params: {
  tripId: string;
  familyOrgCode: string;
  childId: string;
}): Promise<{ ok: boolean; tripId?: string; childId?: string; message?: string }> {
  try {
    const token = await auth.currentUser?.getIdToken();
    if (!token) return { ok: false, message: 'Your session expired. Sign in again.' };
    const res = await fetch(`${FAMILIES_BASE}/api/families/school-link/add-to-trip`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(params),
    });
    const json = await res.json().catch(() => ({} as Record<string, unknown>));
    if (!res.ok || json.ok === false) {
      return {
        ok: false,
        message:
          (typeof json.message === 'string' && json.message) ||
          'We could not add that child to the trip.',
      };
    }
    return json as { ok: boolean; tripId?: string; childId?: string };
  } catch {
    return { ok: false, message: 'Could not reach Trasse — check your connection and try again.' };
  }
}
